import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import Cookies from 'js-cookie';
import { csrfFetch } from '../utils/csrfFetch';
import { buildApiUrl } from '../config';

const AppSettingsContext = createContext();

export const AppSettingsProvider = ({ children }) => {
  const [appSettings, setAppSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  const fetchAppSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl('/appsettings'), {
        credentials: 'include', // This sends cookies automatically
        headers: {
          'accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      setAppSettings(data);
    } catch (err) {

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitAppSettings = async (settings) => {
    try {
  
      setLoading(true);
      setError(null);
      
      const response = await csrfFetch('/appsettings', {
        method: 'POST',
        credentials: 'include', // This sends cookies automatically
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseText = await response.text();

      
      // API returns empty response for both create and update operations
      // Update local state with the settings we sent
      setAppSettings(settings);
      return settings;
    } catch (err) {

      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch settings on mount and when authentication changes
    if (isAuthenticated) {
      fetchAppSettings();
    }
  }, [isAuthenticated]);

  const value = {
    appSettings,
    loading,
    error,
    refetch: fetchAppSettings,
    submitAppSettings,
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
